"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendMessage } from "@/actions/chat";
import { getChatLinkPreview } from "@/actions/chat-link-preview";
import { chatKeys } from "@/features/chat/queries/keys";
import type { SignedLinkPreview } from "@/validations/chat";

/**
 * Send a page that is not a share-card entity (an event's menu, say) to chats
 * as a link message — the same message a person would get by pasting it.
 *
 * The preview is fetched ONCE, signed, and attached to every send, so each
 * bubble is born with its card at its final height (see `sendMessage`); if
 * the preview can't be had, the server's own backfill unfurls it later.
 */
export function useSendLinkToChats() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      url,
      chatIds,
      comment,
    }: {
      url: string;
      chatIds: number[];
      comment?: string;
    }) => {
      let linkPreview: SignedLinkPreview | undefined;
      try {
        const result = await getChatLinkPreview({ url });
        if ("data" in result && result.data) linkPreview = result.data;
      } catch {
        // No card up front; the send-time backfill still unfurls it.
      }
      // The link FIRST: the server attaches a preview only to the first link
      // in the text, and a URL inside the comment would otherwise win.
      const content = comment ? `${url}\n${comment}` : url;
      const results = await Promise.all(
        chatIds.map((chatId) =>
          sendMessage({
            chatId,
            content,
            linkPreview,
            clientMessageId: crypto.randomUUID(),
          }),
        ),
      );
      // Partial success is success: the sheet says "Sent" and the chats that
      // took it show it. Only a send that reached nobody is an error.
      const errors = results.flatMap((result) =>
        "error" in result && result.error ? [result.error] : [],
      );
      if (errors.length === results.length)
        throw new Error(errors[0] ?? "Failed to send");
      return results;
    },
    onSettled: async (_result, _error, variables) => {
      await Promise.all([
        ...variables.chatIds.map((chatId) =>
          queryClient.invalidateQueries({
            queryKey: chatKeys.chatMessages(chatId),
          }),
        ),
        queryClient.invalidateQueries({ queryKey: chatKeys.userChats("all") }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.userChats("archived"),
        }),
        queryClient.invalidateQueries({
          queryKey: chatKeys.totalUnreadCount(),
        }),
      ]);
    },
  });
}
